import * as React from 'react';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ClientsIcon from '@mui/icons-material/People';
import JobsIcon from '@mui/icons-material/Construction';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import UsersIcon from '@mui/icons-material/ManageAccounts';
import TechniciansIcon from '@mui/icons-material/Engineering';
import ClientRequestsIcon from '@mui/icons-material/PersonAdd';
import AlarmIcon from '@mui/icons-material/Alarm';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import MapIcon from '@mui/icons-material/Map';
import { getActiveBottomTabId } from '../utils/mobileBottomTabs';

const TAB_ICONS = {
  dashboard: DashboardIcon,
  clients: ClientsIcon,
  jobs: JobsIcon,
  accounting: AccountBalanceIcon,
  users: UsersIcon,
  technicians: TechniciansIcon,
  requests: ClientRequestsIcon,
  reminders: AlarmIcon,
  notifications: NotificationsIcon,
  calendar: CalendarIcon,
  map: MapIcon,
};

export default function MobileBottomNav({
  tabs,
  visible,
  currentPath,
  onNavigate,
  badgeCounts = {},
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const activeTabId = getActiveBottomTabId(currentPath, tabs);

  if (!tabs.length) return null;

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit timeout={{ enter: 280, exit: 220 }}>
      <Box
        component="nav"
        aria-label="Mobile bottom navigation"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.drawer - 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: isDarkMode
            ? '0 -4px 24px rgba(0, 0, 0, 0.35)'
            : '0 -4px 20px rgba(0, 0, 0, 0.08)',
          pb: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${tabs.length}, 1fr)`,
            minHeight: 56,
          }}
        >
          {tabs.map((tab) => {
            const Icon = TAB_ICONS[tab.id];
            const isActive = activeTabId === tab.id;
            const badgeCount = badgeCounts[tab.id] || 0;

            return (
              <Box
                key={tab.id}
                component="button"
                type="button"
                onClick={() => onNavigate(tab.path)}
                aria-label={tab.title}
                aria-current={isActive ? 'page' : undefined}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.25,
                  py: 0.75,
                  px: 0.5,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                  transition: 'color 0.2s ease, transform 0.15s ease',
                  '&:active': {
                    transform: 'scale(0.96)',
                  },
                }}
              >
                <Badge
                  badgeContent={badgeCount}
                  color="error"
                  max={99}
                  invisible={!badgeCount}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.65rem',
                      minWidth: 16,
                      height: 16,
                      top: 2,
                      right: -2,
                    },
                  }}
                >
                  {Icon ? (
                    <Icon sx={{ fontSize: 22, display: 'block' }} />
                  ) : null}
                </Badge>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: isActive ? 700 : 500,
                    lineHeight: 1.1,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.shortTitle || tab.title}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Slide>
  );
}
